import React, { FC, useRef, useState } from 'react'
import SubmitButton from 'uiKit/buttons/SubmitButton'
import LoaderSmall from 'uiKit/loaders/loaderSmall'
import { UploadIcon } from 'uiKit/icons/UploadIcon'
import { UploadingStatuses } from '../../helpers/constants'
import classes from './styles.module.scss'

const BRAND_BLUE = '#00A0E3'

interface Props {
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
  isUploading: boolean
  uploadingStatus: UploadingStatuses
  template?: string
  errorText?: string
}

export const BulkInviteUploadTab: FC<Props> = ({
  onUpload,
  isUploading,
  uploadingStatus,
  template,
  errorText,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragActive, setIsDragActive] = useState(false)

  const handleFileSelect = (file?: File) => {
    if (!file || isUploading || !fileInputRef.current) {
      return
    }

    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    fileInputRef.current.files = dataTransfer.files
    onUpload({ target: fileInputRef.current } as React.ChangeEvent<HTMLInputElement>)
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()

    if (!isUploading) {
      setIsDragActive(true)
    }
  }

  const handleDragLeave = () => {
    setIsDragActive(false)
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragActive(false)
    handleFileSelect(event.dataTransfer.files[0])
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const downloadTemplate = () => {
    if (!template) {
      return
    }

    const link = document.createElement('a')
    link.setAttribute('download', 'Template.csv')
    link.href = template
    link.target = '_self'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className={classes.container}>
      <p className={classes.uploadLabel}>Upload file</p>

      <div
        className={`${classes.dropzone} ${isDragActive ? classes.dropzoneActive : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isUploading ? (
          <div className={classes.loaderWrap}>
            <LoaderSmall showLoader />
          </div>
        ) : (
          <UploadIcon color="#2E2E32" width={20} height={20} />
        )}

        <p className={classes.mainText}>Drag &amp; drop or upload a CSV file</p>
        <p className={classes.subText}>
          Please select a single csv file to proceed <br />
          Maximum user invitation limit: <span className={classes.subTextBold}>1,000 users</span>
        </p>

        {template && (
          <button
            type="button"
            className={classes.downloadLink}
            onClick={downloadTemplate}
            disabled={isUploading}
          >
            Download template file
          </button>
        )}

        <SubmitButton
          title="Upload file"
          type="primary"
          styles={{
            borderRadius: 8,
            paddingLeft: 46,
            paddingRight: 46,
            paddingTop: 12,
            paddingBottom: 12,
            background: BRAND_BLUE,
            backgroundColor: BRAND_BLUE,
            borderColor: BRAND_BLUE,
          }}
          onClick={handleUploadClick}
          disabled={isUploading}
        />
      </div>

      <input
        ref={fileInputRef}
        className={classes.hiddenInput}
        type="file"
        accept=".csv"
        onChange={onUpload}
        onClick={event => {
          const input = event.target as HTMLInputElement
          input.value = ''
        }}
      />

      {uploadingStatus === UploadingStatuses.ERROR && errorText && (
        <p className={classes.errorText}>{errorText}</p>
      )}
    </div>
  )
}
